// Shared helpers & category metadata for the Laporan (Reports) feature.
import { Activity, ClipboardList, HeartPulse, Users, BedDouble, TrendingUp, UsersRound, FlaskConical, ScanLine, Pill } from "lucide-react";

// Custom SIPERKASA illustrations (generated, on-brand) — served locally.
export const SIPERKASA_HERO_IMG = "/assets/siperkasa/hero.jpeg";
export const SIPERKASA_KUNJUNGAN_IMG = "/assets/siperkasa/kunjungan.jpeg";
export const SIPERKASA_BOR_IMG = "/assets/siperkasa/bor.jpeg";
export const SIPERKASA_PEGAWAI_IMG = "/assets/siperkasa/pegawai.jpeg";
export const SIPERKASA_LAB_IMG = "/assets/siperkasa/lab.jpeg";
export const SIPERKASA_RADIOLOGI_IMG = "/assets/siperkasa/radiologi.jpeg";
export const SIPERKASA_APOTEK_IMG = "/assets/siperkasa/apotek.jpeg";

export const LAPORAN_CATEGORIES = [
  {
    slug: "pmkp",
    icon: HeartPulse,
    key_title: "pmkp_title",
    key_desc: "pmkp_desc",
    nav_key: "laporan_pmkp",
    accent: "from-rose-500 to-pink-600",
    tint: "bg-rose-50 text-rose-700",
  },
  {
    slug: "rawat-jalan",
    icon: ClipboardList,
    key_title: "rj_title",
    key_desc: "rj_desc",
    nav_key: "laporan_rj",
    accent: "from-blue-600 to-cyan-600",
    tint: "bg-blue-50 text-blue-800",
  },
  {
    slug: "rawat-inap",
    icon: Activity,
    key_title: "ri_title",
    key_desc: "ri_desc",
    nav_key: "laporan_ri",
    accent: "from-emerald-500 to-teal-600",
    tint: "bg-emerald-50 text-emerald-700",
  },
  {
    slug: "kunjungan",
    icon: Users,
    key_title: "kunjungan_title",
    key_desc: "kunjungan_desc",
    nav_key: "laporan_kunjungan",
    accent: "from-violet-500 to-fuchsia-600",
    tint: "bg-violet-50 text-violet-700",
  },
];

// Analisis / SIPERKASA data sources (protected — needs admin login).
// Each entry maps to backend `sub` slug.
export const ANALISIS_CATEGORIES = [
  {
    slug: "kunjungan",
    icon: TrendingUp,
    key_title: "analisis_kunjungan_title",
    key_desc: "analisis_kunjungan_desc",
    nav_key: "analisis_kunjungan",
    accent: "from-indigo-500 to-blue-600",
    tint: "bg-indigo-50 text-indigo-700",
    img: SIPERKASA_KUNJUNGAN_IMG,
  },
  {
    slug: "bor",
    icon: BedDouble,
    key_title: "analisis_bor_title",
    key_desc: "analisis_bor_desc",
    nav_key: "analisis_bor",
    accent: "from-amber-500 to-orange-600",
    tint: "bg-amber-50 text-amber-700",
    img: SIPERKASA_BOR_IMG,
  },
  {
    slug: "pegawai",
    icon: UsersRound,
    key_title: "analisis_pegawai_title",
    key_desc: "analisis_pegawai_desc",
    nav_key: "analisis_pegawai",
    accent: "from-emerald-500 to-teal-600",
    tint: "bg-emerald-50 text-emerald-700",
    img: SIPERKASA_PEGAWAI_IMG,
  },
];

// Fasilitas Penunjang — Laboratorium, Radiologi, Apotek (also via SIRS API).
export const PENUNJANG_CATEGORIES = [
  {
    slug: "lab",
    icon: FlaskConical,
    key_title: "analisis_lab_title",
    key_desc: "analisis_lab_desc",
    nav_key: "analisis_lab",
    accent: "from-cyan-500 to-sky-600",
    tint: "bg-cyan-50 text-cyan-700",
    img: SIPERKASA_LAB_IMG,
  },
  {
    slug: "radiologi",
    icon: ScanLine,
    key_title: "analisis_radiologi_title",
    key_desc: "analisis_radiologi_desc",
    nav_key: "analisis_radiologi",
    accent: "from-violet-500 to-purple-600",
    tint: "bg-violet-50 text-violet-700",
    img: SIPERKASA_RADIOLOGI_IMG,
  },
  {
    slug: "apotek",
    icon: Pill,
    key_title: "analisis_apotek_title",
    key_desc: "analisis_apotek_desc",
    nav_key: "analisis_apotek",
    accent: "from-rose-500 to-pink-600",
    tint: "bg-rose-50 text-rose-700",
    img: SIPERKASA_APOTEK_IMG,
  },
];

// Alias — SIPERKASA "core" data sources.
export const SIPERKASA_CATEGORIES = ANALISIS_CATEGORIES;

// All SIPERKASA sources (core + penunjang) for slug lookup.
const ALL_SIPERKASA = [...ANALISIS_CATEGORIES, ...PENUNJANG_CATEGORIES];

export const CATEGORY_BY_SLUG = Object.fromEntries(
  LAPORAN_CATEGORIES.map((c) => [c.slug, c])
);

export const ANALISIS_BY_SLUG = Object.fromEntries(
  ALL_SIPERKASA.map((c) => [c.slug, c])
);

export const MONTH_NAMES_ID = [
  "",
  "Januari",
  "Februari",
  "Maret",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Agustus",
  "September",
  "Oktober",
  "November",
  "Desember",
];

export const MONTH_NAMES_EN = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const formatPeriod = (year, month, lang = "id") => {
  const names = lang === "en" ? MONTH_NAMES_EN : MONTH_NAMES_ID;
  return `${names[month] || month} ${year}`;
};

export const buildPdfUrl = (pdfUrl) => {
  if (!pdfUrl) return null;
  if (/^https?:\/\//i.test(pdfUrl)) return pdfUrl;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${pdfUrl.startsWith("/") ? "" : "/"}${pdfUrl}`;
};
